import { Suspense, lazy, useEffect } from 'react';
import { Title } from './screens/Title';
import { useStore } from './store';

// Only the title screen ships in the first download. Everything else loads
// when first opened, and is prefetched while the title screen sits idle.
const loadBoard = () => import('./screens/Board');
const loadNewGame = () => import('./screens/NewGame');
const Board = lazy(() => loadBoard().then((m) => ({ default: m.Board })));
const NewGame = lazy(() => loadNewGame().then((m) => ({ default: m.NewGame })));
const Online = lazy(() => import('./screens/Online').then((m) => ({ default: m.Online })));
const Lobby = lazy(() => import('./screens/Lobby').then((m) => ({ default: m.Lobby })));

function prefetchLikelyScreens(): void {
  const idle = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1200));
  idle(() => {
    void loadNewGame();
    void loadBoard();
  });
}

export function App() {
  const screen = useStore((s) => s.screen);
  const startGame = useStore((s) => s.startGame);
  const toggleFast = useStore((s) => s.toggleFast);

  // Dev convenience: /?autostart[&seed=N&players=N&difficulty=X&bots&fast&humans=N] jumps straight to a board.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const screen = q.get('screen');
    if (screen === 'online' || screen === 'new') useStore.setState({ screen });
    if (!q.has('autostart')) return;
    const n = Math.min(4, Math.max(1, Number(q.get('players') ?? 3)));
    const colors = ['cyan', 'magenta', 'amber', 'violet'] as const;
    const names =
      Number(q.get('humans') ?? 1) > 1
        ? ['Ada', 'Ripley', 'Selene', 'Rick']
        : ['You', 'Ripley', 'Selene', 'Rick'];
    startGame({
      mapId: 'world',
      difficulty:
        (q.get('difficulty') as 'calm' | 'uneasy' | 'outbreak' | 'apocalypse') ?? 'outbreak',
      players: Array.from({ length: n }, (_, i) => ({
        id: `p${i + 1}`,
        name: names[i]!,
        color: colors[i]!,
        kind: i < Number(q.get('humans') ?? 1) && !q.has('bots') ? 'human' : 'bot',
      })),
      objectives: { eradication: n === 1 },
      seed: Number(q.get('seed') ?? 42),
    });
    if (q.has('fast')) toggleFast();
  }, [startGame, toggleFast]);

  useEffect(prefetchLikelyScreens, []);

  if (screen === 'title') return <Title />;
  return (
    <Suspense fallback={<div className="screen-loading" aria-busy="true" />}>
      {screen === 'new' && <NewGame />}
      {screen === 'online' && <Online />}
      {screen === 'lobby' && <Lobby />}
      {screen === 'board' && <Board />}
    </Suspense>
  );
}
